export type CollateralMapPolicyKind =
  | 'parcel_boundary'
  | 'parcel_if_documented'
  | 'location_reference'
  | 'not_applicable';

export type CollateralMapPolicy = {
  canonicalType: 'land' | 'building' | 'vehicle' | 'deposit' | 'other' | 'unknown';
  kind: CollateralMapPolicyKind;
  label: string;
  guidance: string;
  pdfDisclosure: string;
};

export type CollateralMapEvidenceInput = {
  collateralType?: string | null;
  buildingSubType?: string | null;
  landTitleNumber?: string | null;
  documentNumber?: string | null;
};

export type CollateralMapEvidenceCapability = CollateralMapPolicy & {
  hasDocumentReference: boolean;
  allowsBoundary: boolean;
  unavailableReason: 'workflow_only' | 'missing_land_evidence' | 'location_reference' | 'not_applicable';
};

function normalizedValue(value?: string | null): string {
  return String(value ?? '').trim().toLowerCase();
}

function hasMeaningfulValue(value?: string | null): boolean {
  return normalizedValue(value).length > 0;
}

/**
 * Normalizes stored, legacy, and presentation aliases without inferring an
 * unknown type as land. Unknown values intentionally remain conservative.
 */
export function normalizeCollateralMapType(value?: string | null): CollateralMapPolicy['canonicalType'] {
  switch (normalizedValue(value)) {
    case 'ທີ່ດິນ':
    case 'land':
      return 'land';
    case 'ເຮືອນ':
    case 'ເຮືອນ/ອາຄານ':
    case 'ອາຄານ/ຫ້ອງການ':
    case 'building':
      return 'building';
    case 'ລົດຍົນ':
    case 'ລົດ':
    case 'ຍານພາຫະນະ':
    case 'vehicle':
      return 'vehicle';
    case 'ເງິນຝາກ':
    case 'deposit':
      return 'deposit';
    case 'ອື່ນໆ':
    case 'ອື່ນ':
    case 'other':
      return 'other';
    default:
      return 'unknown';
  }
}

/**
 * Presentation semantics only. This function never infers ownership or alters
 * stored GeoJSON; server write authorization remains a separate mandatory gate.
 */
export function getCollateralMapPolicy(
  collateralType?: string | null,
  buildingSubType?: string | null,
): CollateralMapPolicy {
  const canonicalType = normalizeCollateralMapType(collateralType);
  const subType = normalizedValue(buildingSubType);

  if (canonicalType === 'land') {
    return {
      canonicalType,
      kind: 'parcel_boundary',
      label: 'ຂອບເຂດຕອນດິນ',
      guidance: 'ແຕ້ມຕາມຂອບເຂດຕອນດິນທີ່ບັນທຶກໃນລະບົບ; ບໍ່ແມ່ນການຢືນຢັນກຳມະສິດ.',
      pdfDisclosure: 'ຮູບແຜນທີ່ນີ້ສະແດງຂອບເຂດຕອນດິນຕາມຂໍ້ມູນທີ່ບັນທຶກໃນລະບົບ ບໍ່ແມ່ນການຮັບຮອງກຳມະສິດ.',
    };
  }

  if (canonicalType === 'building') {
    if (subType === 'with_land') {
      return {
        canonicalType,
        kind: 'parcel_boundary',
        label: 'ຂອບເຂດຕອນດິນ + ສິ່ງປຸກສ້າງ',
        guidance: 'ແຕ້ມຕາມຂອບເຂດຕອນດິນ; ບໍ່ໃຫ້ຕີຄວາມເປັນຮູບຮ່າງ ຫຼື ແປນອາຄານ.',
        pdfDisclosure: 'ສຳລັບດິນພ້ອມສິ່ງປຸກສ້າງ, ແຜນທີ່ນີ້ສະແດງຂອບເຂດຕອນດິນ ບໍ່ແມ່ນແບບແປນອາຄານ.',
      };
    }
    if (subType === 'no_land') {
      return {
        canonicalType,
        kind: 'location_reference',
        label: 'ຈຸດອ້າງອີງຫ້ອງຊຸດ/ອາພາດເມ້ນ',
        guidance: 'ແຜນທີ່ໃຊ້ລະບຸຕຳແໜ່ງອາຄານເທົ່ານັ້ນ; ບໍ່ໃຊ້ແຕ້ມຂອບເຂດຕອນດິນ.',
        pdfDisclosure: 'ສຳລັບຫ້ອງຊຸດ/ອາພາດເມ້ນທີ່ບໍ່ມີດິນ, ແຜນທີ່ເປັນຈຸດອ້າງອີງອາຄານ ບໍ່ແມ່ນຂອບເຂດທີ່ດິນ.',
      };
    }
    return {
      canonicalType,
      kind: 'parcel_if_documented',
      label: 'ຕອນດິນ/ຈຸດຕັ້ງອາຄານ',
      guidance: 'ເຮືອນ/ອາຄານລາຍການນີ້ຍັງບໍ່ລະບຸປະເພດດິນ. ແຕ້ມຂອບເຂດໄດ້ສະເພາະເມື່ອມີເລກອ້າງອີງຕອນດິນ; ບໍ່ແມ່ນແປນອາຄານ.',
      pdfDisclosure: 'ສຳລັບເຮືອນ/ອາຄານທີ່ subtype ບໍ່ຄົບ, ແຜນທີ່ເປັນຂອບເຂດຕອນດິນ ຫຼື ຈຸດອ້າງອີງຕາມຂໍ້ມູນບັນທຶກ ບໍ່ແມ່ນແບບແປນອາຄານ.',
    };
  }

  if (canonicalType === 'deposit') {
    return {
      canonicalType,
      kind: 'not_applicable',
      label: 'ບໍ່ໃຊ້ແຜນທີ່ສຳລັບເງິນຝາກ',
      guidance: 'ຫຼັກຊັບເງິນຝາກບໍ່ມີຂອບເຂດພູມສາດ; ຈຶ່ງບໍ່ສາມາດແຕ້ມ ຫຼື ແກ້ໄຂ boundary ໄດ້.',
      pdfDisclosure: 'ຫຼັກຊັບເງິນຝາກບໍ່ໃຊ້ຂອບເຂດແຜນທີ່.',
    };
  }

  const isUnknown = canonicalType === 'unknown';
  return {
    canonicalType,
    kind: 'location_reference',
    label: isUnknown ? 'ປະເພດຫຼັກຊັບຍັງບໍ່ຖືກຮອງຮັບ' : 'ຈຸດອ້າງອີງທີ່ຕັ້ງ',
    guidance: isUnknown
      ? 'ບໍ່ສາມາດແຕ້ມ boundary ໄດ້ ເນື່ອງຈາກປະເພດຫຼັກຊັບຍັງບໍ່ມີນະໂຍບາຍແຜນທີ່.'
      : 'ແຜນທີ່ໃຊ້ເປັນຈຸດອ້າງອີງເທົ່ານັ້ນ; ບໍ່ໃຊ້ແຕ້ມຂອບເຂດຕອນດິນ.',
    pdfDisclosure: isUnknown
      ? 'ປະເພດຫຼັກຊັບນີ້ຍັງບໍ່ມີນະໂຍບາຍ boundary; ແຜນທີ່ເປັນພຽງຂໍ້ມູນອ້າງອີງ.'
      : 'ສຳລັບຫຼັກຊັບປະເພດນີ້, ແຜນທີ່ເປັນຈຸດອ້າງອີງ ບໍ່ແມ່ນຂອບເຂດທີ່ດິນ.',
  };
}

export function getCollateralMapEvidenceCapability(
  input: CollateralMapEvidenceInput,
): CollateralMapEvidenceCapability {
  const policy = getCollateralMapPolicy(input.collateralType, input.buildingSubType);
  const hasDocumentReference = hasMeaningfulValue(input.landTitleNumber) || hasMeaningfulValue(input.documentNumber);
  const allowsBoundary = policy.kind === 'parcel_boundary'
    || (policy.kind === 'parcel_if_documented' && hasDocumentReference);

  return {
    ...policy,
    hasDocumentReference,
    allowsBoundary,
    unavailableReason: allowsBoundary
      ? 'workflow_only'
      : policy.kind === 'parcel_if_documented'
        ? 'missing_land_evidence'
        : policy.kind === 'not_applicable'
          ? 'not_applicable'
          : 'location_reference',
  };
}
